import { useEffect, useState } from "react";
import RightModal from "../components/templates/RightModal";
import { getEquipmentsList, selectEquipmentsData, selectEquipmentLoading } from "../redux/equipment/equipmentSlice";
import DynamicIcon from "../components/DynamicIcon";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import AddEditEquipment from "../components/equipmentPage/AddEditEquipment";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import { useColorPalette } from '../hooks/useColorPalette';
import DataTable from "../components/DataTable/DataTable";

export default function EquipmentsList() {
    const dispatch = useDispatch();
    const palette = useColorPalette();
    const equipmentsList = useSelector(selectEquipmentsData);
    const equipmentsLoading = useSelector(selectEquipmentLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [searchTerm, setSearchTerm] = useState('');
    const [dataTableKey, setDataTableKey] = useState(Date.now());
    
    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalEquipmentId = useSelector(selectRightModalId);
    
    // Gestione della paginazione con DataTable
    const handlePageChange = (pageIndex, pageSize) => {
        console.log('Cambio pagina a:', pageIndex, 'dimensione:', pageSize);
        // Calcolo l'offset in base all'indice della pagina e alla dimensione
        const start = pageIndex * pageSize;
        
        dispatch(getEquipmentsList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            start,
            length: pageSize
        }));
        dispatch(resetRefreshNow());
    };
    
    // Gestione della ricerca
    const handleSearch = (searchValue) => {
        console.log('Ricerca:', searchValue);
        setSearchTerm(searchValue);
        
        if (searchValue.length >= 3 || searchValue.length === 0) {
            dispatch(getEquipmentsList({
                accountId: accountData?._id,
                token,
                search: searchValue.length >= 3 ? searchValue : '',
                start: 0,
                length: 10
            }));
            dispatch(resetRefreshNow());
        }
    };

    // Eseguo la chiamata iniziale quando il componente viene montato
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getEquipmentsList({
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 3 ? searchTerm : '',
                start: 0, // Inizio da zero
                length: 10 // Uso la dimensione della pagina predefinita
            }));
            dispatch(resetRefreshNow());
            
            // Aggiorna la DataTable quando cambia il termine di ricerca
            if (searchTerm.length >= 3 || searchTerm === '') {
                setDataTableKey(Date.now());
            }
        }
    }, [dispatch, accountData, token, searchTerm]);
    

    
    // Aggiorna la tabella quando il modal viene chiuso
    useEffect(() => {
        if (!rightModalIsOpen && dataTableKey) {
            // Aggiorna la tabella
            setDataTableKey(Date.now());
        }
    }, [rightModalIsOpen]);
    
    // Avvio il refresh dei dati quando isRefreshNow della RightModal si attiva
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow) handlePageChange(0, 10);
    }, [rightModalIsRefreshNow]);
    
    return (
        <div className="page">
            {equipmentsLoading && <LoadingSection isFixed />}
            <h1><span className="font-light">Attrezzature / </span>Lista Attrezzature</h1>
            <div className="box">
                {/* Nuova attrezzatura */}
                <div className="flex justify-end mb-4">
                    <button 
                        className="primary" 
                        onClick={() => dispatch(setModal({ mode: 'add' }))}
                    >
                        <DynamicIcon iconClass="fa-solid fa-plus" />
                        Nuova Attrezzatura
                    </button>
                </div>

                {/* DataTable per le attrezzature */}
                <div className="w-full my-4 overflow-x-auto">
                    <DataTable
                        key={dataTableKey}
                        columns={[
                            { 
                                id: 'title', 
                                title: 'NOME',
                                width: '80%',
                                render: (equipment) => (
                                    <div className="text-left uppercase">
                                        {equipment.title || '-'}
                                    </div>
                                )
                            },
                            { 
                                id: 'actions', 
                                title: 'AZIONI',
                                width: '20%',
                                render: (equipment) => (
                                    <div className="flex gap-3 justify-center">
                                        <button 
                                            style={{ color: 'inherit' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(setModal({
                                                    isOpen: true,
                                                    mode: 'view',
                                                    id: equipment._id
                                                }));
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-eye" />
                                        </button>
                                        <button 
                                            style={{ color: 'inherit' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(setModal({
                                                    isOpen: true,
                                                    mode: 'edit',
                                                    id: equipment._id
                                                }));
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-edit" />
                                        </button>
                                    </div>
                                )
                            }
                        ]}
                        data={equipmentsList?.data || []}
                        loading={equipmentsLoading}
                        totalRecords={equipmentsList?.recordsFiltered || 0}
                        pageSize={10}
                        onPageChange={handlePageChange}
                        hasSearch={true}
                        onSearch={handleSearch}
                        initialSearchTerm={searchTerm}
                        emptyMessage="Nessuna attrezzatura trovata"
                        renderCustomRow={(equipment, index, isEven, tableStyles) => {
                            // Funzione per gestire il click sulla riga
                            const handleRowClick = () => {
                                dispatch(setModal({
                                    isOpen: true,
                                    mode: 'view',
                                    id: equipment._id
                                }));
                            };
                            
                            return (
                                <div 
                                    key={equipment._id}
                                    className="data-table-row cursor-pointer"
                                    style={{
                                        backgroundColor: isEven ? tableStyles.evenRow.backgroundColor : tableStyles.oddRow.backgroundColor,
                                        color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color,
                                    }}
                                    onClick={handleRowClick}
                                >
                                    {/* Colonna NOME */}
                                    <div className="data-table-cell" style={{ width: '80%' }} data-label="NOME">
                                        <div className="text-left uppercase">
                                            {equipment.title || '-'}
                                        </div>
                                    </div>
                                    
                                    {/* Colonna AZIONI */}
                                    <div className="data-table-cell" style={{ width: '20%' }} data-label="AZIONI">
                                        <div className="flex gap-3 justify-center">
                                            <button 
                                                style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    dispatch(setModal({
                                                        isOpen: true,
                                                        mode: 'view',
                                                        id: equipment._id
                                                    }));
                                                }}
                                            >
                                                <DynamicIcon iconClass="fa-solid fa-eye" />
                                            </button>
                                            <button 
                                                style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    dispatch(setModal({
                                                        isOpen: true,
                                                        mode: 'edit',
                                                        id: equipment._id
                                                    }));
                                                }}
                                            >
                                                <DynamicIcon iconClass="fa-solid fa-edit" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        }}
                    />
                </div>
            </div>

            {/* RIGHT MODAL per inserimento o modifica attrezzatura */}
            {
                rightModalIsOpen && (
                    <RightModal>
                        {(rightModalMode === 'add' || rightModalMode === 'edit') && (
                            <AddEditEquipment equipmentId={rightModalEquipmentId} />
                        )}
                        {
                            rightModalMode === 'view' && rightModalEquipmentId && (
                                <div>{rightModalEquipmentId}</div>
                            )
                        }
                    </RightModal>
                )
            }
        </div>
    );
}
